import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { CardMember } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import MemberCard from "@/components/feature/MemberCard";
import MemberCardModal from "@/components/feature/MemberCardModal";

export default function CardSection() {
  const { data, loading, error, reload } = useApi<CardMember[]>("cards.php?size=100");
  const cards = data ?? [];
  const [openNo, setOpenNo] = useState<number | null>(null);

  const openIndex = cards.findIndex((c) => c.no === openNo);
  const handlePrev = () => {
    if (openIndex < 0) return;
    setOpenNo(cards[(openIndex - 1 + cards.length) % cards.length].no);
  };
  const handleNext = () => {
    if (openIndex < 0) return;
    setOpenNo(cards[(openIndex + 1) % cards.length].no);
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              회원 명함
            </h2>
            <p className="mt-2 text-foreground-600">
              회원님들의 디지털 명함을 만나보세요.
            </p>
          </div>
          <Link
            to="/cards"
            className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
          >
            전체보기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[150px] w-full rounded-[14px]" />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="명함을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : cards.length === 0 ? (
          <EmptyState icon="ri-id-card-line" title="아직 등록된 명함이 없습니다" />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => (
              <MemberCard key={card.no} card={card} onOpen={(c) => setOpenNo(c.no)} />
            ))}
          </div>
        )}
      </div>

      <MemberCardModal
        no={openNo}
        onClose={() => setOpenNo(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
}